import { FC, ReactNode } from "react";

import { useMultiStyleConfig, Box } from "@chakra-ui/react";
import { Formik, FormikHelpers } from "formik";

import Loader from "@components/Loader";
import { useRouter } from "@lib/router";
import { gtmEcommercePusher, gtmEventEcommerceData, STEPS, EVENTS } from "@modules/gtm";
import mapOrderValuesToGTMValues from "@modules/gtm/mapOrderValuesToGTMValues";
import { ROUTES } from "@utils/constants";

import { DeliveryMethod, OrderFields, OrderStep, SHIPMENT_STEP_DELIVERY_METHODS } from "../constants";
import useOrderData from "../context/useOrderData";
import useOrderPayment from "../hooks/useOrderPayment";
import { orderFormSchema, TOrderFormData } from "../models/orderForm";
import PackageInfo from "./packageInfo/PackageInfo";
import Receiver from "./receiver/Receiver";
import Sender from "./sender/Sender";
import Shipment from "./shipment/Shipment";
import Summary from "./summary/Summary";

type TOrderPageContentProps = {
  currentStep: OrderStep;
};

const stepComponentsMap = {
  [OrderStep.PackageInfo]: PackageInfo,
  [OrderStep.ShipmentDetails]: Shipment,
  [OrderStep.SenderDetails]: Sender,
  [OrderStep.ReceiverDetails]: Receiver,
  [OrderStep.Summary]: Summary,
};

const getPageContent = (step: OrderStep): ReactNode => {
  const StepComponent = stepComponentsMap[step];
  return StepComponent ? <StepComponent /> : null;
};

const OrderPageContent: FC<TOrderPageContentProps> = ({ currentStep }) => {
  const styles = useMultiStyleConfig("Order", {});

  const {
    initialValues,
    isShipmentSaveLoading,
    saveShipment,
    isShipmentEditLoading,
    isLoadingAddresses,
    dynamicData: { packageSizes },
    staticData: { paymentMethods },
  } = useOrderData();

  const router = useRouter();

  const { isLoading: isPaymentLoading, executeOrderPayment } = useOrderPayment();
  const handleSubmit = async (values: TOrderFormData, { setFieldValue }: FormikHelpers<TOrderFormData>) => {
    const deliveryMethod = values[OrderFields.DELIVERY_METHOD] as DeliveryMethod;

    if (currentStep === OrderStep.PackageInfo) {
      gtmEcommercePusher(
        gtmEventEcommerceData(
          mapOrderValuesToGTMValues(values, packageSizes, paymentMethods),
          STEPS.ONE,
          EVENTS.CHECKOUT
        )
      );
      router.push(
        SHIPMENT_STEP_DELIVERY_METHODS.includes(deliveryMethod) ? ROUTES.ORDER_SHIPMENT : ROUTES.ORDER_SENDER
      );
      return;
    }

    if (currentStep === OrderStep.SenderDetails) {
      gtmEcommercePusher(
        gtmEventEcommerceData(
          mapOrderValuesToGTMValues(values, packageSizes, paymentMethods),
          STEPS.TWO,
          EVENTS.CHECKOUT
        )
      );

      router.push(ROUTES.ORDER_RECEIVER);
      return;
    }

    if (currentStep === OrderStep.ReceiverDetails || currentStep === OrderStep.ShipmentDetails) {
      if (currentStep === OrderStep.ShipmentDetails) {
        gtmEcommercePusher(
          gtmEventEcommerceData(
            mapOrderValuesToGTMValues(values, packageSizes, paymentMethods),
            STEPS.TWO,
            EVENTS.CHECKOUT
          )
        );
      }
      gtmEcommercePusher(
        gtmEventEcommerceData(
          mapOrderValuesToGTMValues(values, packageSizes, paymentMethods),
          STEPS.THREE,
          EVENTS.CHECKOUT
        )
      );
      router.push(ROUTES.ORDER_SUMMARY);
      return;
    }

    // Invoice data changed so save shipment and then proceed to payment
    if (currentStep === OrderStep.Summary) {
      gtmEcommercePusher(
        gtmEventEcommerceData(
          mapOrderValuesToGTMValues(values, packageSizes, paymentMethods),
          STEPS.FOUR,
          EVENTS.CHECKOUT
        )
      );
      const { isSuccess: isSaveSuccess, shipmentId } = await saveShipment(values);
      if (!isSaveSuccess) {
        return;
      }
      setFieldValue(OrderFields.SHIPMENT_ID, shipmentId);

      const paymentMethod = values[OrderFields.PAYMENT_METHOD];
      executeOrderPayment(shipmentId!, paymentMethod!);
    }
  };

  return (
    <Box sx={styles.pageContent}>
      <Formik
        initialValues={initialValues as any}
        validateOnChange={false}
        validateOnBlur
        validationSchema={orderFormSchema}
        onSubmit={handleSubmit}
      >
        {() => (
          <Loader
            sx={{ height: "100%" }}
            isLoading={isShipmentEditLoading || isShipmentSaveLoading || isPaymentLoading || isLoadingAddresses}
          >
            {getPageContent(currentStep)}
          </Loader>
        )}
      </Formik>
    </Box>
  );
};
export default OrderPageContent;
